import { useEffect, useRef, useState } from 'react'
import { FileText, StickyNote } from 'lucide-react'
import { MentionTextarea } from '@/components/notes/MentionTextarea'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { formatNoteWhen } from '@/lib/utils/noteFormatting'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import type { PmsAuditPage, PmsAuditRow } from '../types'

function formatChangeMessage(notes: Record<string, unknown> | undefined): string {
  if (!notes || typeof notes !== 'object') return ''
  const entries = Object.entries(notes)
  if (entries.length === 1 && entries[0][0] === 'note') {
    const value = entries[0][1]
    if (Array.isArray(value)) return String(value[1] ?? value[0] ?? '')
    return String(value ?? '')
  }
  return entries
    .map(([key, value]) => {
      if (Array.isArray(value) && value.length >= 2) {
        return `${key} : ${value[0] ?? '—'} => ${value[1] ?? '—'}`
      }
      if (Array.isArray(value) && value.length === 1) {
        return `${key} : ${value[0] ?? '—'}`
      }
      return `${key} : ${value ?? '—'}`
    })
    .join('\n')
}

function emptyPage(page: number, perPage: number): PmsAuditPage {
  return { rows: [], total: 0, page, perPage, lastPage: 1 }
}

export function PmsAuditLogSection({
  title = 'Audit Logs',
  rows,
  loadPage,
  reloadKey,
  onSave,
  testId = 'pms-audit-log-section',
}: {
  title?: string
  rows?: PmsAuditRow[]
  loadPage?: (page: number, perPage: number) => Promise<PmsAuditPage>
  reloadKey?: string | number
  onSave?: (note: string) => Promise<void>
  testId?: string
}) {
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [result, setResult] = useState<PmsAuditPage>(() => emptyPage(1, 15))
  const [refreshToken, setRefreshToken] = useState(0)
  const loadPageRef = useRef(loadPage)
  loadPageRef.current = loadPage

  useEffect(() => {
    setPage(1)
  }, [reloadKey])

  useEffect(() => {
    const loader = loadPageRef.current
    if (!loader) return
    let cancelled = false
    void loader(page, perPage)
      .then((next) => {
        if (!cancelled) setResult(next)
      })
      .catch(() => {
        if (!cancelled) setResult(emptyPage(page, perPage))
      })
    return () => {
      cancelled = true
    }
  }, [page, perPage, reloadKey, refreshToken])

  const visibleRows = loadPage ? result.rows : rows || []
  const total = loadPage ? result.total : (rows || []).length
  const lastPage = loadPage ? result.lastPage : 1

  const saveNote = async () => {
    const note = draft.trim()
    if (!note || !onSave || saving) return
    setSaving(true)
    try {
      await onSave(note)
      setDraft('')
      showSuccess('Note saved.')
      setPage(1)
      setRefreshToken((value) => value + 1)
    } catch (error) {
      showApiError(error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section
      data-testid={testId}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-[0_4px_12px_rgba(15,23,42,0.05)]"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="m-0 flex items-center gap-2.5 text-base font-bold uppercase tracking-tight text-slate-900">
          <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full border border-[#c7ddff] bg-[#dbeafe] text-[#2563eb]">
            <StickyNote className="h-4 w-4" />
          </span>
          {title}
        </h2>
        {onSave ? (
          <button
            type="button"
            disabled={!draft.trim() || saving}
            onClick={() => void saveNote()}
            className={cn(primaryEntityActionButtonClass, 'whitespace-nowrap')}
            data-testid={`${testId}-save`}
          >
            {saving ? 'Please wait…' : 'Save'}
          </button>
        ) : null}
      </div>

      {onSave ? (
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          onTaggingAdd={() => undefined}
          rows={5}
          disabled={saving}
          className="min-h-[7.5rem] w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[rgb(21,151,255)] focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
          placeholder="Add note/message (use @ to mention Liberty users)"
          dataTestId={`${testId}-textarea`}
        />
      ) : null}

      <div className="mt-6">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black">
          Audit History
        </h3>
        {visibleRows.length === 0 ? (
          <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-6 text-center text-sm text-slate-500">
            No notes or messages.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-sky-100 bg-sky-50/80">
            <ul className="divide-y divide-sky-100/80 px-2 py-3 sm:px-4">
              {visibleRows.map((entry, index) => (
                <li
                  key={`${entry.updated_at}-${index}`}
                  className="grid grid-cols-1 gap-3 py-3 text-sm text-slate-700 first:pt-1 sm:grid-cols-12 sm:gap-0"
                >
                  <div className="flex gap-2 border-sky-200 sm:col-span-3 sm:border-r sm:pr-3">
                    <span className="mt-0.5 shrink-0 text-slate-500" aria-hidden>
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-800">
                        {entry.updated_by || 'System'}
                      </div>
                      <div className="mt-1 text-xs text-slate-600">
                        {formatNoteWhen(entry.updated_at)}
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 break-words border-sky-200 px-0 text-xs font-medium text-slate-800 sm:col-span-2 sm:border-r sm:px-3">
                    {entry.entity_label || '\u00A0'}
                  </div>
                  <div className="min-w-0 whitespace-pre-wrap break-words pl-0 text-slate-800 sm:col-span-7 sm:pl-4 sm:pr-2">
                    {formatChangeMessage(entry.notes)}
                  </div>
                </li>
              ))}
            </ul>
            {loadPage && total > 0 ? (
              <ListPaginationBar
                placement="footer"
                currentPage={result.page}
                lastPage={lastPage}
                totalRecords={total}
                perPage={perPage}
                onPageChange={setPage}
                onPerPageChange={(next) => {
                  setPerPage(next)
                  setPage(1)
                }}
                testIdPrefix={testId}
                className="rounded-b-lg bg-white"
              />
            ) : null}
          </div>
        )}
      </div>
    </section>
  )
}
